<template>
	<div :class="{ 'bg-white rounded-xl shadow-xl p-5 mx-2': showResults || showQuiz }">
		<div v-for="(question, questionIndex) in questions" :key="question._id">
			<div
				v-if="questionIndex === currentQuestion && showQuiz"
				ref="question"
				class="text-start scroll-mt-10"
			>
				<div class="flex items-center space-x-3 mb-4">
					<HokIcon name="icon:lightbulb" color="main" />
					<p class="font-bold text-main mb-0">
						Frage {{ questionIndex + 1 }}/{{ questions.length }}
					</p>
				</div>
				<div class="flex flex-col items-start mb-4">
					<p class="mb-4 font-normal text-lg text-text">"{{ question.question }}"</p>
					<HokRadioGroup class="w-full">
						<HokRadio
							v-for="(answer, index) in question.options"
							:id="`answer-${index}`"
							:key="index"
							:name="answer.option"
							:value="answer.option"
							:inline="false"
							styling="none"
							:disabled="questionAnswered && index !== answerIndex"
							emit-change
							@change="answerQuestion(question, answer, index)"
						>
							<div
								class="bg-grey-lightest p-2 mb-2 rounded-md cursor-pointer"
								:class="[
									{
										'border-2 border-main': questionAnswered && index === answerIndex
									},
									{
										'border-2 border-purple':
											!yesOrNoQuiz &&
											!weightedQuiz &&
											questionAnswered &&
											index === answerIndex &&
											!answerResult
									}
								]"
							>
								{{ answer.option }}
							</div>

							<div
								v-if="questionAnswered && index === answerIndex && calculateQuestionSum(question)"
								class="relative flex items-center"
							>
								<div class="w-full bg-grey-lightest rounded-full h-5">
									<transition
										appear
										@before-appear="percentageBeforeEnter"
										@after-appear="
											percentageEnter(
												$event,
												calculatePercentage(answer.totalAnswers, calculateQuestionSum(question))
											)
										"
									>
										<div class="h-5 rounded-full w-full" :class="barStyling(answer, index)" />
									</transition>
								</div>
								<div class="absolute font-bold right-2">
									{{ calculatePercentage(answer.totalAnswers, calculateQuestionSum(question)) }}%
									aller Antworten
								</div>
							</div>
						</HokRadio>
					</HokRadioGroup>
					<template v-if="explanation">
						<h3
							v-if="answerCorrectOrIncorrect && !weightedQuiz"
							class="flex items-center"
							:class="answerCorrectOrIncorrect.className"
						>
							<HokIcon
								:name="answerCorrectOrIncorrect.icon"
								:color="answerCorrectOrIncorrect.color"
								class="mr-2"
							/>{{ answerCorrectOrIncorrect.title }}
						</h3>
						<p :class="{ 'py-4': weightedQuiz }">{{ explanation }}</p>
					</template>
				</div>
				<div v-if="questionAnswered" class="flex justify-center pb-8">
					<HokButton color="main" @click="nextQuestion">Weiter</HokButton>
				</div>
			</div>
		</div>
		<div v-if="showResults" class="w-full flex flex-col items-center">
			<h2 id="results">{{ results.title }}</h2>
			<div
				v-if="!weightedQuiz"
				class="w-full bg-grey-lightest rounded-full h-6 relative flex items-center mb-4"
			>
				<transition
					appear
					@before-appear="percentageBeforeEnter"
					@after-appear="
						percentageEnter(
							$event,
							calculatePercentage(quizScore, weightedQuiz ? maxPoints : questions.length)
						)
					"
				>
					<div class="bg-main h-6 rounded-full" />
				</transition>
				<div class="absolute right-2 font-bold">
					{{ calculatePercentage(quizScore, weightedQuiz ? maxPoints : questions.length) }} %
				</div>
			</div>

			<h5 class="border-b border-grey-lightest pb-4 font-normal text-start">
				{{ results.text }}
			</h5>
			<div class="mb-4" :class="{ 'flex space-x-2  justify-between': !$isMobile.any }">
				<HokButton
					v-if="!isPwa"
					class="mb-2"
					fullwidth="mobile"
					color="main"
					:to="'/pwa/login'"
					@click="linkClicked('login')"
				>
					Anmelden</HokButton
				>
				<HokButton
					:to="isPwa ? '/pwa' : '/jobs'"
					class="mb-2"
					fullwidth="mobile"
					:outline="!isPwa"
					color="main"
					@click="linkClicked('jobs')"
				>
					Jobsuche</HokButton
				>
				<HokButton
					:to="isPwa ? '/pwa/profile/magazine' : '/karriere'"
					class="mb-2"
					fullwidth="mobile"
					outline
					color="main"
					@click="linkClicked('magazine')"
				>
					Karriere-Tipps</HokButton
				>
			</div>
			<HokButton
				class="mx-2 pb-8"
				size="small"
				color="text"
				fullwidth="mobile"
				is-text
				@click="resetQuiz"
			>
				Quiz nochmal starten</HokButton
			>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import { lsTest } from '../helpers/localstorage';
import HokRadio from './HokRadio.vue';
import HokRadioGroup from './HokRadioGroup.vue';

function isHTMLElement(element: any): element is HTMLElement {
	return element && element instanceof HTMLElement;
}

export default defineComponent({
	name: 'JobQuiz',
	components: {
		HokRadio,
		HokRadioGroup
	},
	emits: ['reset', 'track-redirect', 'finished', 'track-question'],
	data() {
		const answeredQuestions: { questionId: string; answerId: string }[] = [];

		return {
			currentQuestion: 0,
			explanation: '',
			answerResult: false,
			questionAnswered: false,
			answerIndex: 0,
			quizScore: 0,
			answeredQuestions
		};
	},
	computed: {
		maxPoints() {
			let maxPoints = 0;
			this.questions.forEach(q => {
				maxPoints += q.options.length - 1;
			});
			return maxPoints;
		},
		results(): { title: string; text: string } {
			let results: { title; text };
			if (this.weightedQuiz) {
				const pointsPerLevel = this.maxPoints / this.quizResults.length;
				const quizResultIndex = Math.min(
					this.quizResults.length - 1,
					Math.max(0, this.quizResults.length - 1 - Math.floor(this.quizScore / pointsPerLevel))
				);
				return {
					title: this.quizResults[quizResultIndex].title,
					text: this.quizResults[quizResultIndex].text
				};
			}
			if (this.yesOrNoQuiz) {
				switch (true) {
					case this.quizScore <= this.questions.length / 2:
						results = {
							title: this.quizResults[1].title,
							text: this.quizResults[1].text
						};
						break;
					case this.quizScore <= this.questions.length:
					default:
						results = {
							title: this.quizResults[0].title,
							text: this.quizResults[0].text
						};
						break;
				}
			} else {
				switch (true) {
					case this.quizScore <= Math.floor(this.questions.length / 4):
						results = {
							title: this.quizResults[0].title,
							text: this.quizResults[0].text
						};
						break;
					case this.quizScore <= Math.floor(this.questions.length / 2):
						results = {
							title: this.quizResults[1].title,
							text: this.quizResults[1].text
						};
						break;
					case this.quizScore <= Math.floor(this.questions.length * (3 / 4)):
						results = {
							title: this.quizResults[2].title,
							text: this.quizResults[2].text
						};
						break;
					case this.quizScore <= this.questions.length:
					default:
						results = {
							title: this.quizResults[3].title,
							text: this.quizResults[3].text
						};
						break;
				}
			}
			return results;
		},
		answerCorrectOrIncorrect(): { title; icon; color; className } {
			const text = { title: '', icon: '', color: '', className: '' };
			if (this.answerResult) {
				text.title = 'Richtig!';
				text.icon = 'icon:swipe-right';
				text.color = 'main';
				text.className = 'text-main';
			} else {
				text.title = 'Falsch!';
				text.icon = 'icon:swipe-left';
				text.color = 'purple';
				text.className = 'text-purple';
			}
			return text;
		}
	},
	mounted() {
		if (lsTest() && localStorage.getItem(`quizScore-${this.quizId}`)) {
			this.quizScore = Number(localStorage.getItem(`quizScore-${this.quizId}`));
			this.$emit('finished');
		}
		if (this.shuffledAnswers) {
			this.questions.forEach(q => {
				q.options = this.shuffleAnswers(q.options);
			});
		}
	},
	methods: {
		shuffleAnswers(
			answers
		): { correct: boolean; explanation: string; option: string; totalAnswers: number }[] {
			for (let i = answers.length - 1; i > 0; i -= 1) {
				const j = Math.floor(Math.random() * (i + 1));
				const temp = answers[i];
				answers[i] = answers[j];
				answers[j] = temp;
			}
			return answers;
		},
		answerQuestion(question, answer, index) {
			this.answerResult = answer.correct;
			if (this.weightedQuiz) {
				this.quizScore += answer.points;
			} else if (answer.correct === true) {
				this.quizScore += 1;
			}
			this.explanation = answer.explanation;
			this.questionAnswered = true;
			this.answerIndex = index;
			this.answeredQuestions.push({ questionId: question._id, answerId: answer._id });
			this.$emit('track-question', this.currentQuestion);
		},
		nextQuestion() {
			if (this.currentQuestion < this.questions.length - 1) {
				this.currentQuestion += 1;
			} else {
				this.$emit('finished', this.answeredQuestions);
				if (lsTest()) {
					localStorage.setItem(`quizScore-${this.quizId}`, JSON.stringify(this.quizScore));
				}
			}
			this.questionAnswered = false;
			this.explanation = '';
			if (this.$isMobile.any) {
				const element = this.$refs.question;
				if (isHTMLElement(element?.[0])) {
					element?.[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}
		},
		calculateQuestionSum(question): number {
			let sum = 0;
			for (let i = 0; i < question.options.length; i += 1) {
				sum += question.options[i].totalAnswers;
			}
			return sum;
		},
		calculatePercentage(dividend, divisor): number {
			return Math.round((dividend / divisor) * 100);
		},
		barStyling(answer, index): string {
			if (
				!this.yesOrNoQuiz &&
				!this.weightedQuiz &&
				answer.correct === false &&
				this.questionAnswered &&
				index === this.answerIndex
			) {
				return 'bg-purple';
			}
			return 'bg-main';
		},
		resetQuiz() {
			this.currentQuestion = 0;
			this.quizScore = 0;
			this.answeredQuestions = [];
			if (lsTest()) {
				localStorage.removeItem(`quizScore-${this.quizId}`);
			}
			this.$emit('reset');
		},
		linkClicked(redirect) {
			this.$emit('track-redirect', redirect);
		},
		percentageBeforeEnter(el) {
			if (el?.style) {
				el.style.width = 0;
			}
		},
		percentageEnter(el, percentage) {
			if (el?.style) {
				el.style.width = `${percentage}%`;
				el.style.transition = `width 0.6s linear`;
			}
		}
	},
	props: {
		questions: {
			type: Array as PropType<
				{
					_id: string;
					question: string;
					options: {
						correct: boolean;
						explanation: string;
						option: string;
						totalAnswers: number;
					}[];
				}[]
			>,
			required: true
		},
		quizResults: { type: Array as PropType<{ title: string; text: string }[]>, required: true },
		quizIcon: { type: String, required: false },
		quizId: { type: String, required: true },
		isPwa: { type: Boolean, default: true },
		showQuiz: { type: Boolean, default: false },
		showResults: { type: Boolean, default: false },
		shuffledAnswers: { type: Boolean, default: true },
		yesOrNoQuiz: { type: Boolean, default: false },
		weightedQuiz: { type: Boolean, default: false }
	}
});
</script>
